import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface AlertThreshold {
  metric: string;
  label: string;
  threshold: number;
  enabled: boolean;
  updated_at: string;
}

export interface AlertLog {
  id: number;
  metric: string;
  metric_date: string;
  threshold: number;
  actual_value: number;
  level: string;
  event_type: string | null;
  message: string;
  created_at: string;
}

export interface CheckResult {
  created: number;
  recovered: number;
}

interface ListResponse {
  thresholds: AlertThreshold[];
  logs: AlertLog[];
}

async function invokeAlerts<T>(payload: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("monitor-alerts", { body: payload });
  if (error) {
    let message = error.message || "操作失败，请稍后重试";
    try {
      const context = (error as { context?: Response }).context;
      if (context) {
        const parsed = await context.json();
        if (parsed?.error) message = parsed.error;
      }
    } catch {
      // keep original message
    }
    throw new Error(message);
  }
  const result = data as { error?: string } | null;
  if (result?.error) throw new Error(result.error);
  return data as T;
}

interface UseMonitorAlertsResult {
  thresholds: AlertThreshold[];
  logs: AlertLog[];
  loading: boolean;
  error: string | null;
  checking: boolean;
  reload: () => void;
  runCheck: () => Promise<CheckResult>;
  saveThresholds: (rows: { metric: string; threshold: number; enabled: boolean }[]) => Promise<number>;
}

export function useMonitorAlerts(): UseMonitorAlertsResult {
  const [thresholds, setThresholds] = useState<AlertThreshold[]>([]);
  const [logs, setLogs] = useState<AlertLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await invokeAlerts<ListResponse>({ action: "list" });
      setThresholds(data.thresholds ?? []);
      setLogs(data.logs ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "告警数据加载失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const runCheck = useCallback(async () => {
    setChecking(true);
    try {
      const result = await invokeAlerts<CheckResult>({ action: "check" });
      await load();
      return { created: result.created ?? 0, recovered: result.recovered ?? 0 };
    } finally {
      setChecking(false);
    }
  }, [load]);

  const saveThresholds = useCallback(
    async (rows: { metric: string; threshold: number; enabled: boolean }[]) => {
      const result = await invokeAlerts<{ updated: number }>({ action: "setThresholds", thresholds: rows });
      await load();
      return result.updated ?? 0;
    },
    [load],
  );

  return { thresholds, logs, loading, error, checking, reload: load, runCheck, saveThresholds };
}